import {
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultSizeStyle,
  ArrowShapeArrowheadStartStyle,
  ArrowShapeArrowheadEndStyle,
  ArrowShapeKindStyle,
} from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { MeasurementUnit } from "../shared/mark-presentation";
export type DrawingDefaults = {
  color: string;
  weight: number;
  opacity: number;
  size: "s" | "m" | "l" | "xl";
  dash: "solid" | "dashed" | "dotted";
  fill: "none" | "semi" | "solid" | "pattern";
  start: "none" | "arrow" | "bar" | "dot";
  end: "none" | "arrow" | "bar" | "dot";
  arrow: "arc" | "elbow";
  hatch: "none" | "solid" | "diagonal" | "cross";
  hatchSpacing: number;
  hatchAngle: number;
  fillColor: string;
  unit: MeasurementUnit;
  precision: number;
  labelOffset: number;
  endStyle: "tick" | "bar" | "arrow" | "none";
  reflex: boolean;
  arcRadius: number;
};
const styles = new WeakMap<Editor, Map<string, DrawingDefaults>>();
const initial = new WeakMap<Editor, { color: string; unit: MeasurementUnit }>();
export const nativeColor = (color: string) =>
  (
    ({
      "#d65331": "red",
      "#25343b": "black",
      "#517691": "blue",
      "#657a4c": "green",
      "#9b6589": "violet",
    }) as const
  )[color as "#d65331"] ||
  (["red", "black", "blue", "green", "violet"].includes(color)
    ? (color as "black")
    : "black");
export function initializeDrawingDefaults(
  editor: Editor,
  color: string,
  unit: MeasurementUnit,
) {
  initial.set(editor, { color, unit });
}
export function drawingDefaults(editor: Editor, tool: string): DrawingDefaults {
  let map = styles.get(editor);
  if (!map) {
    map = new Map();
    styles.set(editor, map);
  }
  let value = map.get(tool);
  if (!value) {
    const prefs = initial.get(editor);
    value = {
      color: prefs?.color || "#d65331",
      weight: tool === "line" ? 3 : 1.5,
      opacity: tool === "highlight" ? 0.5 : 1,
      size: "m",
      dash: "solid",
      fill: "none",
      start: "none",
      end: "arrow",
      arrow: "arc",
      hatch: tool === "polygon" ? "solid" : "diagonal",
      hatchSpacing: 9,
      hatchAngle: 45,
      fillColor: prefs?.color || "#d65331",
      unit: prefs?.unit || "m",
      precision: tool === "angle" ? 1 : 2,
      labelOffset: -12,
      endStyle: "tick",
      reflex: false,
      arcRadius: 32,
    };
    map.set(tool, value);
  }
  return value;
}
export function updateDrawingDefaults(
  editor: Editor,
  tool: string,
  patch: Partial<DrawingDefaults>,
) {
  Object.assign(drawingDefaults(editor, tool), patch);
  applyDrawingDefaults(editor, tool);
}
export function applyDrawingDefaults(editor: Editor, tool: string) {
  const s = drawingDefaults(editor, tool);
  editor.setStyleForNextShapes(DefaultColorStyle, nativeColor(s.color));
  editor.setStyleForNextShapes(DefaultDashStyle, s.dash);
  editor.setStyleForNextShapes(DefaultSizeStyle, s.size);
  editor.setStyleForNextShapes(DefaultFillStyle, s.fill);
  editor.setStyleForNextShapes(ArrowShapeArrowheadStartStyle, s.start);
  editor.setStyleForNextShapes(ArrowShapeArrowheadEndStyle, s.end);
  editor.setStyleForNextShapes(ArrowShapeKindStyle, s.arrow);
  editor.setOpacityForNextShapes(s.opacity);
}
